import { Address_Country as Country } from "gen/proto/address/models_pb"

export const CountryNames = {
  [Country.US]: "United States",
  [Country.CA]: "Canada",
} as const

export type CountryName = (typeof CountryNames)[keyof typeof CountryNames]

export function googleMapsCountryCode(country: Country): string {
  if (country === Country.CA) return "ca"
  return "us"
}

export function countryNameFromPB(country: Country): CountryName {
  if (country === Country.CA) {
    return CountryNames[Country.CA]
  }
  return CountryNames[Country.US]
}

export function countryFromName(name: CountryName): Country {
  if (name === CountryNames[Country.CA]) {
    return Country.CA
  }
  return Country.US
}

export function validatePostalCode(postalCode: string): string | null {
  return [Country.US, Country.CA].some(
    (country) => validateCountryPostalCode(country, postalCode) === null
  )
    ? null
    : "Invalid postal code"
}

export function validateCountryPostalCode(
  country: Country,
  postalCode: string
): string | null {
  const caRegex =
    /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][ ]?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i
  const usRegex = /^[0-9]{5}(-[0-9]{4})?$/

  if (country === Country.CA) {
    if (caRegex.test(postalCode)) {
      return null
    }
    return "Postal code must be in the format A1A 1A1 (e.g. K1A 0B1) or A1A1A1 (e.g. K1A0B1)"
  }
  if (usRegex.test(postalCode)) {
    return null
  }
  return "Postal code must be 5 digits (e.g. 94027) or 9 digits (5-4 format, e.g. 94027-1234)"
}

export const US_STATES = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FM",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MH",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "MP",
  "OH",
  "OK",
  "OR",
  "PW",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VI",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
] as const

export const CA_PROVINCES = [
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
] as const

const usStatesSet = new Set<string>(US_STATES)
const caProvincesSet = new Set<string>(CA_PROVINCES)

export function validState(country: Country, state: string): string | null {
  const upper = state.toUpperCase()
  if (country === Country.CA) {
    if (caProvincesSet.has(upper)) {
      return null
    }
    return "Invalid Canadian province"
  }
  if (usStatesSet.has(upper)) {
    return null
  }
  return "Invalid US state"
}
